import React, { memo, useEffect, useState, type FC, type ReactNode } from 'react';
import { TouchableOpacity, type ViewStyle } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon } from '@/components/ui/svg-icon';
import { Text } from '@/components/ui/text';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

export type CheckboxValue = boolean;

export type CheckboxProps = {
  /** `true` = tick, `false` = empty, `'indeterminate'` = hybrid (dash). */
  checked?: CheckboxValue;
  onValueChange?: (value: CheckboxValue) => void;
  disabled?: boolean;
  /** Label next to the tick box (Figma: "Level 1"). */
  label?: string;
  /** Custom content on the right to replace the label. */
  children?: ReactNode;
  /** Draws a red border around the tick box (e.g. unaccepted required terms). */
  error?: boolean;
  style?: ViewStyle | ViewStyle[];
};

function getNextCheckboxValue(value: CheckboxValue): CheckboxValue {
  return !value;
}

export const Checkbox: FC<CheckboxProps> = memo(
  ({ checked, onValueChange, disabled = false, label, children, error = false, style }) => {
    const [value, setValue] = useState<CheckboxValue>(checked ?? false);
    const isControlled = checked !== undefined;
    const isChecked = isControlled ? checked : value;
    const styles = useStyles();
    const { scale, isTablet } = useScaling();
    const BOX_SIZE = scale(24);

    useEffect(() => {
      if (!isControlled) return;
      setValue(checked ?? false);
    }, [checked, isControlled]);

    const handlePress = () => {
      if (disabled) return;
      const nextValue = getNextCheckboxValue(isChecked);
      if (!isControlled) {
        setValue(nextValue);
      }
      onValueChange?.(nextValue);
    };

    return (
      <TouchableOpacity
        onPress={handlePress}
        disabled={disabled}
        activeOpacity={0.7}
        style={[styles.root, disabled && styles.disabled, style]}
        accessibilityRole="checkbox"
        accessibilityState={{
          checked: isChecked ? true : false,
          disabled,
        }}>
        <Stack align="center" justify="center" width={BOX_SIZE} height={BOX_SIZE}>
          {isChecked ? (
            <SvgIcon name="IcCheckboxChecked" size={scale(24)} color={colors['0']} />
          ) : (
            // The box outline uses `currentColor`; turn it red to flag an error.
            <SvgIcon
              name="IcCheckboxUnchecked"
              size={scale(24)}
              color={error ? colors.Error : guardianColors[5]}
            />
          )}
        </Stack>
        {label != null ? (
          <Text
            variant={
              isTablet ? 'Font Inter/Subtitle/Regular 14px' : 'Font Inter/Subtitle/Regular 12px'
            }
            style={styles.label}
            numberOfLines={1}>
            {label}
          </Text>
        ) : (
          children
        )}
      </TouchableOpacity>
    );
  },
);

Checkbox.displayName = 'Checkbox';

const useStyles = createStyles(({ scale }) => {
  const BOX_SIZE = scale(24);
  const BORDER_RADIUS = scale(4);
  const GAP = scale(7);

  return {
    root: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: GAP,
    },
    disabled: {
      opacity: 0.6,
    },
    box: {
      width: BOX_SIZE,
      height: BOX_SIZE,
      borderRadius: BORDER_RADIUS,
      alignItems: 'center',
      justifyContent: 'center',
    },
    boxUnchecked: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: colors['10'],
    },
    boxChecked: {
      backgroundColor: colors.Blue,
      borderWidth: 0,
    },
    checkmark: {
      color: colors['0'],
      fontSize: scale(14),
      fontWeight: '700',
    },
    indeterminateMark: {
      color: colors['0'],
      fontSize: scale(14),
      fontWeight: '700',
    },
    label: {
      color: colors['10'],
      flex: 1,
    },
  };
});
